import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import Button from 'src/components/Button';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { TransportType } from 'src/types/transport';
import getCountryOptions from 'src/utils/countryOptions';
import { BuildingIcon } from 'src/components/SkladonIcon/SmallIcons';
import { DeliveredIcon } from 'src/components/SkladonIcon/BigIcons';
import Typography from 'src/components/Typography';
import { IconTextStyle, StyledIcon } from './Recipient';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';

export interface SenderPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: TransportType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    permissionEditAll: boolean;
    onSetProviderCompany: () => void;
    providerCompaniesData: null | ProviderCompanyType[];
    summary?: boolean;
}

const Sender: FC<SenderPropsType> = (props) => {
    return (
        <Grid>
            <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                <Typography variant="h2" gutterBottom>
                    {translate('transportDetail.senderInfo')}
                    {!props.summary && !!props.providerCompaniesData?.length && !props.disableEdit && (
                        <Button
                            onClick={props.onSetProviderCompany}
                            className="order-select-provider-button"
                            mobileFullWidht
                        >
                            {translate('transportDetail.showContacts')}
                        </Button>
                    )}
                </Typography>
            </GridItem>
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <BuildingIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('transportDetail.contact')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_NAME}
                            error={props.editErrors.SENDER_NAME}
                            onChange={(value, error) => props.onChange('SENDER_NAME', value, error)}
                            name="SENDER_NAME"
                            label={translate('transportDetail.senderFullName')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_ATTENTION_NAME}
                            error={props.editErrors.SENDER_ATTENTION_NAME}
                            onChange={(value, error) => props.onChange('SENDER_ATTENTION_NAME', value, error)}
                            name="SENDER_ATTENTION_NAME"
                            label={translate('transportDetail.attentionName')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_COMPANY_NAME}
                            error={props.editErrors.SENDER_COMPANY_NAME}
                            onChange={(value, error) => props.onChange('SENDER_COMPANY_NAME', value, error)}
                            name="SENDER_COMPANY_NAME"
                            label={translate('transportDetail.companyName')}
                            fullWidth
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_EMAIL}
                            error={props.editErrors.SENDER_EMAIL}
                            onChange={(value, error) => props.onChange('SENDER_EMAIL', value, error)}
                            name="SENDER_EMAIL"
                            label={translate('transportDetail.email')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            type="email"
                            maxLength={50}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_PHONE}
                            error={props.editErrors.SENDER_PHONE}
                            onChange={(value, error) => props.onChange('SENDER_PHONE', value, error)}
                            name="SENDER_PHONE"
                            label={translate('transportDetail.phone')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            maxLength={14}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <DeliveredIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('transportDetail.senderAddress')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_STREET}
                            error={props.editErrors.SENDER_STREET}
                            onChange={(value, error) => props.onChange('SENDER_STREET', value, error)}
                            name="SENDER_STREET"
                            label={translate('transportDetail.senderStreet')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_CITY}
                            error={props.editErrors.SENDER_CITY}
                            onChange={(value, error) => props.onChange('SENDER_CITY', value, error)}
                            name="SENDER_CITY"
                            label={translate('transportDetail.city')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            maxLength={30}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.SENDER_POST_CODE}
                            error={props.editErrors.SENDER_POST_CODE}
                            onChange={(value, error) => props.onChange('SENDER_POST_CODE', value, error)}
                            name="SENDER_POST_CODE"
                            label={translate('transportDetail.postCode')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                            maxLength={9}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <Select
                            options={getCountryOptions()}
                            value={props.editData.SENDER_STATE}
                            error={props.editErrors.SENDER_STATE}
                            onChange={(value, error) => props.onChange('SENDER_STATE', value, error)}
                            name="SENDER_STATE"
                            label={translate('transportDetail.country')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
        </Grid>
    );
};

export default Sender;
